import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { UrlBuilder } from '../../core';
import { GridDetails, ActionType, GridHeader } from '../../sample-tracking/tracking-overview/interfaces/grid';
import { ShipmentData } from '../interfaces/shipment-data';
import { ActivityLogger } from '../../activities/activity-logger';
import { RangeKind, DateRangeFilterValue } from '../../shared/time-frame-filter/timeframe-filter.component';
import * as moment from 'moment';
import { SiteShippingToolStorage } from '../service/site-shipping-tool-storage';
import { concatMap, switchMap, map } from 'rxjs/operators';
import { iif, Observable, of } from 'rxjs';
import { ShipmentFormService } from '../shipment-form/service/shipment-form-service';
import { ShipmentFormStorage } from '../shipment-form/shipment-form-storage';
import { ConfirmDialogRequest } from '../shared/confirm-dialog/confirm-dialog.component';
import { InvestigatorSitesStorage } from '../../sites';
import { UserStorage } from '../../user';


@Component({
    selector: 'shipment-list',
    template: require('./shipment-list.component.html'),
    styles: [require('./shipment-list.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ShipmentListComponent {

    public isLoadingCompleted = false;
    public isInprogressListLoadingComplete = false;
    public inProgressDataGridDetails: GridDetails;
    public inProgressData: ShipmentData[] = [];
    public inProgressListHeader: GridHeader[] = [];
    public selectedRows: ShipmentData[] = [];
    public currentUserEmail: string;
    public timeFrameRanges: RangeKind[] = [
        { key: 'today', value: 'Today', selected: false },
        { key: 'weekly', value: 'Last 7 days', selected: false },
        { key: 'monthly', value: 'Last 30 days', selected: false },
        { key: 'twoMonths', value: 'Last 60 days', selected: false },
        { key: 'quarterly', value: 'Last 90 days', selected: false },
    ];
    public selectedDateRange: DateRangeFilterValue;
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public defaultPageSize = 25;
    private isEmulated: boolean;

    constructor(
        private shippingStorage: SiteShippingToolStorage,
        private urlBuilder: UrlBuilder,
        private changeDetector: ChangeDetectorRef,
        private sitesStorage: InvestigatorSitesStorage,
        private activityLogger: ActivityLogger,
        private createShipmentFormService: ShipmentFormService,
        private userStorage: UserStorage,
        private shipmentFormStorage: ShipmentFormStorage,
    ) {
        this.PrepareInProgressHeaderColumn();
        this.selectedDateRange = this.getQuarterlyRange();
        this.loadInProgressShipments();
        this.userStorage.currentUser.subscribe(user => {
            this.currentUserEmail = user.metadata.email;
        });
        this.isEmulated = this.createShipmentFormService.isUserEmulated();
        activityLogger.logActivity('Shipments - List View');
    }

    public onGridItemClick(event: any) {
        this.activityLogger.logActivity('Shipments - Submitted Shipments - Action : View Shipment');
        this.shipmentSummary(event.data.shipmentId);
    }

    public onInProgressGridActionEvent($event: any) {
        if ($event.header.actionType === ActionType.delete) {
            this.handelDeleteShipment($event.data.shipmentId);
            return;
        }
        this.urlBuilder.esa().sampleManagement().siteShipping().resumeShipment($event.data.shipmentId).navigate();
    }

    public viewAllShipments() {
    }

    public loadInProgressShipments() {
        this.sitesStorage.loadSites().subscribe(sites => {
            this.shippingStorage.getInProgressShipmentList(sites).subscribe(gridData => {
                this.inProgressData = this.formatShipmentList(gridData);
                this.inProgressDataGridDetails = { header: this.inProgressListHeader, data: this.inProgressData };
                this.isInprogressListLoadingComplete = true;
                this.changeDetector.markForCheck();
            });
        });
    }

    public timeFrameFilterChange($event: DateRangeFilterValue) {
        if ($event === this.selectedDateRange && $event.rangeType !== 'dateRange') { return; }
        this.isLoadingCompleted = false;
        this.selectedDateRange = $event;
    }

    public createShipment() {
        this.urlBuilder.esa().sampleManagement().siteShipping().createShipment().navigate();
    }

    public shipmentSummary(id: number) {
        this.urlBuilder.esa().sampleManagement().siteShipping().shipmentSummary(id).navigate();
    }

    public onRowsSelected($event: ShipmentData[]) {
        this.selectedRows = $event.filter(t => t.rowSelected);
    }

    private PrepareInProgressHeaderColumn() {
        this.inProgressListHeader = [

            {
                displayText: 'Ship To Organization Name',
                key: 'destinationOrgName',
                sort: null
            },
            {
                displayText: 'Number of Samples Included in Shipment',
                key: 'numberOfContainers',
                defaultValue: '0',
                sort: null
            },
            {
                displayText: 'Protocols',
                key: 'protocolCode',
                sort: null
            },
            {
                displayText: 'Created Date',
                key: 'dateCreated',
                sort: null,
                isDate: true,
            },
            {
                displayText: 'Created by',
                key: 'createdBy',
                sort: null,
            },
            {
                displayText: 'Action',
                key: 'null',
                hideSort: true,
                actions: [
                    {
                        description: 'Resume Shipment',
                        actionType: ActionType.resume,
                        cssClass: 'action resume',
                        displayText: 'Resume',
                        helpTextClass: 'action helptext',
                        disableAction: 'disableAction'
                    },
                    {
                        description: 'Delete Shipment',
                        actionType: ActionType.delete,
                        cssClass: 'action delete',
                        displayText: 'Delete',
                        helpTextClass: 'action helptext',
                        disableAction: 'disableAction'
                    },
                ]
            },
        ];
    }

    private getQuarterlyRange(): DateRangeFilterValue {
        return {
            dateBegin: moment().subtract(90, 'd').format('YYYY-MM-DD'),
            dateEnd: moment().format('YYYY-MM-DD'),
            rangeType: 'quarterly'
        };
    }

    private formatShipmentList(gridData: ShipmentData[]): ShipmentData[] {
        if (!gridData || gridData.length === 0) { return []; }
        let data: ShipmentData[] = [];
        gridData.forEach(shipmentData => {
            let index = data.findIndex(t => t.shipmentId === shipmentData.shipmentId);
            if (index === -1) {
                let filteredData = gridData.filter(t => t.shipmentId === shipmentData.shipmentId);
                shipmentData.numberOfContainers = filteredData && filteredData[0].investigatorSiteShipmentContainerId > 0 ? filteredData.length : 0;
                shipmentData.protocolCode = shipmentData.hasPermission ? shipmentData.protocolCode : null;
                shipmentData.disableAction = shipmentData.createdBy !== this.currentUserEmail;
                data.push(shipmentData);
            } else {
                if (data[index].protocolCode !== shipmentData.protocolCode && shipmentData.hasPermission) {
                    if (shipmentData.protocolCode && data[index].protocolCode) {
                        data[index].protocolCode = data[index].protocolCode + ', ' + shipmentData.protocolCode;
                    } else if (!data[index].protocolCode) {
                        data[index].protocolCode = shipmentData.protocolCode;
                    } 
                }
            }
        });
        return data;
    }

    private handelDeleteShipment(shipmentId: number) {
        this.showWarning(
            {
                message: `Are you sure you want to delete this shipment? <br> <br> <br>
                <b>Note: </b>This action cannot be undone.`,
                reverseButtons: true,
                yesButtonText: 'Delete',
                noButtonText: 'Cancel',
                title: 'Confirm Deletion',
            },
            true
        ).pipe(
            concatMap((dialogResult) =>
                this.deleteInprogressShipment(shipmentId, dialogResult.isYesButtonClicked)
            )
        ).subscribe((res: any) => {
            if (res !== false) {
                this.loadInProgressShipments();
            }
        });
    }

    private deleteInprogressShipment(shipmentId: number, isYesButtonClicked: boolean): Observable<any> {
        this.activityLogger.logActivity('Shipments - In Progress Shipments - Action : Delete Shipment');
        if (isYesButtonClicked && !this.isEmulated) {
            this.isInprogressListLoadingComplete = false;
            this.changeDetector.markForCheck();
            return this.shipmentFormStorage.deleteShipment(shipmentId);
        }
        return of(false);
    }

    private showWarning(data: ConfirmDialogRequest, showDeleteDialog = false) {
        let dialogRequest: ConfirmDialogRequest = { ...data };
        return this.createShipmentFormService.showConfirmDialog(dialogRequest, showDeleteDialog)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult));
    }
}